import React, { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import Datepicker, { DateType } from 'react-tailwindcss-datepicker';
import { parseISO } from 'date-fns';
import { Input } from '../../../components/inputs/input';
import { TextArea } from '../../../components/inputs/textarea/textarea';
import {
  useDeleteStoriesByTripByTripIdStoryAndStoryIdMutation,
  usePatchStoriesByTripByTripIdStoryAndStoryIdMutation,
} from '../../../store/backendApi';
import { FileUploadResult, useUploadFiles } from '../../../utils/upload';
import { InfoAlert } from '../../../components/alerts/info';
import { SuccessAlert } from '../../../components/alerts/success';
import { ErrorAlert } from '../../../components/alerts/error';
import { imgUrl, thumbnail } from '../../../utils/images';
import DynamicImage from '../../../components/dynamicImage';
import path from 'path-browserify';
import { ArrowDownIcon, ArrowUpIcon, TrashIcon } from '@heroicons/react/24/outline';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { Story } from '../../../schema';

export interface StoryElementProps {
  story: Story;
  setEditing: Dispatch<SetStateAction<boolean>>;
  setHasChanges: React.Dispatch<React.SetStateAction<boolean>>;
}

export const EditingStoryElement = (props: StoryElementProps) => {
  const [storyDate, setStoryDate] = useState<{ startDate: DateType; endDate: DateType } | null>(
    props.story.date
      ? { startDate: parseISO(props.story.date), endDate: parseISO(props.story.date) }
      : null,
  );
  const [storyPlace, setStoryPlace] = useState<string | undefined>(props.story.place);
  const [storyTitle, setStoryTitle] = useState<string>(props.story.title);
  const [storyDescription, setStoryDescription] = useState<string | undefined>(
    props.story.description,
  );
  const [patchStory, { isLoading: patchStoryLoading, error: patchStoryError }] =
    usePatchStoriesByTripByTripIdStoryAndStoryIdMutation();
  const [callDeleteStory, { isLoading: deleteStoryLoading }] =
    useDeleteStoriesByTripByTripIdStoryAndStoryIdMutation();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const headerRef = useRef<HTMLHeadingElement>(null);
  const componentRef = useRef<HTMLDivElement>(null);
  const [failedEvent, setFailedEvent] = useState(false);
  const [uploadedMedia, setUploadedMedia] = useState<FileUploadResult[]>(
    props.story.medias.map((u) => ({
      success: true,
      fileUpload: {
        uploadUrl: new URL(imgUrl(props.story.tripId, `${u.id}.${u.ext}`)),
        uploadId: u.id,
        localFile: undefined,
        dimensions: u.dimensions,
      },
    })),
  );
  const [uploadingMediaCount, setUploadingMediaCount] = useState<number | null>(null);
  const [filesToUpload, setFilesToUpload] = useState<File[]>([]);
  const { uploadFiles } = useUploadFiles();
  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);
  const [confirmingDeleteStory, setConfirmingDeleteStory] = useState(false);
  const [addPhotoTileStyle, setAddPhotoTileStyle] = useState<{ height: string; width: string }>({
    height: '200px',
    width: '200px',
  });
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [scrollDirection, setScrollDirection] = useState<'up' | 'down'>('up');
  const [showFullText, setShowFullText] = useState(false);

  // Check if form has changes
  const hasFormChanges = useMemo(() => {
    // Check text fields
    const titleChanged = storyTitle !== props.story.title;
    const descriptionChanged = storyDescription !== props.story.description;
    const placeChanged = storyPlace !== props.story.place;

    // Check date field
    const originalDate = props.story.date ? parseISO(props.story.date) : null;
    const currentDate = storyDate?.startDate;
    const dateChanged =
      originalDate?.toISOString().split('T')[0] !== currentDate?.toISOString().split('T')[0];

    // Check media changes (count or order)
    const originalMediaIds = props.story.medias.map((m) => m.id).sort();
    const currentMediaIds = uploadedMedia
      .filter((m) => m.fileUpload)
      .map((m) => m.fileUpload!.uploadId)
      .sort();
    const mediaChanged = JSON.stringify(originalMediaIds) !== JSON.stringify(currentMediaIds);

    return titleChanged || descriptionChanged || placeChanged || dateChanged || mediaChanged;
  }, [storyTitle, storyDescription, storyPlace, storyDate, uploadedMedia, props.story]);

  // Handle scroll to show/hide floating arrow
  useEffect(() => {
    const handleScroll = () => {
      if (headerRef.current && componentRef.current) {
        const headerRect = headerRef.current.getBoundingClientRect();
        const componentRect = componentRef.current.getBoundingClientRect();

        const isHeaderVisible = headerRect.top < window.innerHeight && headerRect.bottom > 0;
        const isComponentVisible =
          componentRect.top < window.innerHeight && componentRect.bottom > 0;

        if (!isHeaderVisible) {
          // Determine scroll direction based on header position
          const direction = headerRect.top < 0 ? 'up' : 'down';
          setScrollDirection(direction);
          setShowScrollToTop(true);

          // Show full text if entire component is out of view
          setShowFullText(!isComponentVisible);
        } else {
          setShowScrollToTop(false);
          setShowFullText(false);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial state

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to story function
  const scrollToStory = () => {
    if (headerRef.current) {
      const headerOffset = 64; // Account for sticky page header
      const elementPosition = headerRef.current.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Calculate dimensions for the "Add photo" tile based on the first photo
  useEffect(() => {
    const firstPhoto = uploadedMedia[0]?.fileUpload?.dimensions;
    if (firstPhoto) {
      const maxHeight = 200;
      const maxWidthPx = (40 / 100) * window.innerWidth;
      const width = Math.round(maxHeight * (firstPhoto.width / firstPhoto.height));
      const finalWidth = Math.min(width, maxWidthPx);
      const finalHeight =
        width > maxWidthPx
          ? Math.round(maxWidthPx * (firstPhoto.height / firstPhoto.width))
          : maxHeight;
      setAddPhotoTileStyle({ height: `${finalHeight}px`, width: `${finalWidth}px` });
    } else {
      setAddPhotoTileStyle({ height: '200px', width: '200px' });
    }
  }, [uploadedMedia]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setUploadedMedia((items) => {
        const oldIndex = items.findIndex(
          (item) => item.fileUpload!.uploadUrl.pathname === active.id,
        );
        const newIndex = items.findIndex(
          (item) => item.fileUpload!.uploadUrl.pathname === over?.id,
        );

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleDeleteClick = (mediaId: string) => {
    setConfirmingDelete(mediaId);
  };

  const handleConfirmDelete = (mediaId: string) => {
    setUploadedMedia((prev) =>
      prev.filter((media) => media.fileUpload!.uploadUrl.pathname !== mediaId),
    );
    setConfirmingDelete(null);
  };

  const handleCancelDelete = () => {
    setConfirmingDelete(null);
  };

  // Handle clicking outside to cancel confirmation
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (confirmingDelete) {
        const target = event.target as Element;
        // Check if the click is not on the confirmation overlay
        if (!target.closest('.confirmation-overlay')) {
          setConfirmingDelete(null);
        }
      }
    };

    if (confirmingDelete) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [confirmingDelete]);

  const handleDeleteStoryClick = () => {
    setConfirmingDeleteStory(true);
  };

  const handleConfirmDeleteStory = async () => {
    await deleteStory();
  };

  const handleCancelDeleteStory = () => {
    setConfirmingDeleteStory(false);
  };

  const updateStory = async () => {
    const uploads = uploadedMedia
      .filter((media) => media.fileUpload)
      .map((media) => {
        return {
          id: media.fileUpload!.uploadId,
          ext: media.fileUpload!.uploadUrl.pathname.split('.').pop()!,
          dimensions: media.fileUpload!.dimensions,
        };
      });
    await patchStory({
      tripId: props.story.tripId,
      storyId: props.story.id,
      partialStoryBodyRequest: {
        title: storyTitle,
        description: storyDescription ?? '',
        place: storyPlace,
        date: storyDate?.startDate?.toLocaleDateString('sv-SE') ?? undefined,
        medias: uploads,
      },
    });
    props.setHasChanges(true);
    props.setEditing(false);
  };

  const deleteStory = async () => {
    await callDeleteStory({
      tripId: props.story.tripId,
      storyId: props.story.id,
    });
    props.setHasChanges(true);
    props.setEditing(false);
  };

  const showFailure = () => {
    setFailedEvent(true);
    setTimeout(() => {
      setFailedEvent(false);
    }, 2000);
  };

  const onFileUploaded = async (fileUploadResult: FileUploadResult) => {
    if (!fileUploadResult.success) {
      console.error(fileUploadResult.error);
      showFailure();
      return;
    }
    setUploadedMedia((prev) => [fileUploadResult, ...prev]);
    setUploadingMediaCount((prev) => (prev || 0) - 1);
  };

  useEffect(() => {
    if (filesToUpload && filesToUpload.length > 0) {
      uploadFiles({
        tripId: props.story.tripId,
        storyId: props.story.id,
        files: filesToUpload,
        onUploaded: onFileUploaded,
      }).catch((e) => {
        console.error('error uploading files');
        console.error(e);
        showFailure();
      });
      setFilesToUpload([]);
    }
  }, [filesToUpload]);

  return (
    <div
      className="flex flex-col items-center text-center pb-20 relative z-90 bg-gray-100 rounded-xl"
      ref={componentRef}
    >
      <h2 ref={headerRef} className="mt-1 text-2xl/7 text-center font-extrabold">
        Update Story
      </h2>

      <div className="sm:max-w-xs">
        <div className="mt-3">
          <Datepicker
            inputId={'story-date'}
            asSingle={true}
            value={storyDate}
            onChange={(newValue) => setStoryDate(newValue)}
          />
        </div>
        <Input
          label={''}
          id={'story-place'}
          name={'story-place'}
          type={'text'}
          placeholder="Location"
          setValue={setStoryPlace}
          value={storyPlace}
        />
        <Input
          label={''}
          id={'story-title'}
          name={'story-title'}
          type={'text'}
          placeholder="Sunny Breakfast ☀️"
          setValue={setStoryTitle}
          value={storyTitle}
        />
        <TextArea
          label={''}
          id={'stoy-description'}
          name={'description'}
          rows={4}
          placeholder={'We started this sunny morning ❤️at a lovely Cafè in Florence 🥐☕️'}
          value={storyDescription}
          setValue={setStoryDescription}
        />
      </div>
      {!!uploadingMediaCount && uploadingMediaCount > 0 && (
        <InfoAlert text={`Uploading ${uploadingMediaCount} files...`} />
      )}
      {!!uploadingMediaCount && uploadingMediaCount == 0 && <SuccessAlert text="Upload done" />}
      {failedEvent && <ErrorAlert text="Upload failed" />}
      <div className="mt-6 flex flex-wrap gap-1 justify-center">
        <div
          onClick={() => fileInputRef.current?.click()}
          className="rounded-xl border-2 border-dashed border-gray-300 bg-gray-50
          flex items-center justify-center cursor-pointer hover:bg-gray-100 transition-colors"
          style={{
            height: addPhotoTileStyle.height,
            width: addPhotoTileStyle.width,
          }}
        >
          <div className="text-center text-gray-500">
            <div className="text-2xl mb-1">📷</div>
            <div className="text-sm font-medium">Add Photo</div>
          </div>
        </div>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={uploadedMedia.map((media) => media.fileUpload!.uploadUrl.pathname)}
            strategy={rectSortingStrategy}
          >
            {uploadedMedia.map((media) => (
              <div key={media.fileUpload!.uploadUrl.pathname} className="relative">
                <DynamicImage
                  id={media.fileUpload!.uploadUrl.pathname}
                  className="max-h-[200px] w-auto max-w-[40vw] rounded-xl object-cover"
                  src={imgUrl(
                    props.story.tripId,
                    thumbnail(path.basename(media.fileUpload!.uploadUrl.pathname)),
                  )}
                  defaultSrc={'/spinner.gif'}
                  sortable={true}
                />
                {confirmingDelete === media.fileUpload!.uploadUrl.pathname ? (
                  <>
                    <div
                      className="confirmation-overlay absolute inset-0 bg-black bg-opacity-50
                    flex items-center justify-center rounded-xl"
                    >
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleConfirmDelete(media.fileUpload!.uploadUrl.pathname)}
                          className="px-2 py-1 bg-red-700 hover:bg-red-600 text-white rounded"
                        >
                          Delete
                        </button>
                        <button
                          onClick={handleCancelDelete}
                          className="px-2 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <button
                    onClick={() => handleDeleteClick(media.fileUpload!.uploadUrl.pathname)}
                    className="absolute top-2 right-2 bg-gray-800 bg-opacity-75 text-white
                    p-1 rounded hover:bg-opacity-100 transition-all"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </SortableContext>
        </DndContext>
      </div>

      {/* Fixed Bottom Action Bar */}
      <div
        className="fixed bottom-0 left-0 right-0 z-110 bg-black bg-opacity-50
       backdrop-blur-sm px-4 py-3 rounded-t-xl max-w-md mx-auto sm:max-w-lg md:max-w-xl lg:max-w-2xl"
      >
        {confirmingDeleteStory ? (
          <div className="flex gap-3 justify-center">
            <button
              className="px-6 py-2 bg-red-600 text-white rounded-lg font-medium
              hover:bg-red-700 disabled:bg-gray-500 disabled:cursor-not-allowed transition-colors"
              disabled={patchStoryLoading || deleteStoryLoading}
              onClick={handleConfirmDeleteStory}
            >
              {deleteStoryLoading ? 'Deleting...' : 'Click again to confirm delete'}
            </button>
            <button
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium
              hover:bg-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
              disabled={patchStoryLoading || deleteStoryLoading}
              onClick={handleCancelDeleteStory}
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="flex justify-between items-center gap-3">
            <button
              className="flex-1 px-8 py-2 bg-indigo-600 text-white rounded-lg font-medium
              hover:bg-indigo-700 disabled:bg-gray-500 disabled:cursor-not-allowed transition-colors"
              disabled={
                patchStoryLoading ||
                deleteStoryLoading ||
                !hasFormChanges ||
                (uploadingMediaCount ?? 0) > 0
              }
              onClick={() => updateStory()}
            >
              {patchStoryLoading ? 'Saving...' : 'Save'}
            </button>
            <button
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium
              hover:bg-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
              disabled={patchStoryLoading || deleteStoryLoading}
              onClick={() => props.setEditing(false)}
            >
              Cancel
            </button>
            <button
              className="p-2 bg-red-100 text-red-700 rounded-lg font-medium
              hover:bg-red-200 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
              disabled={patchStoryLoading || deleteStoryLoading}
              onClick={handleDeleteStoryClick}
            >
              <TrashIcon className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>

      {/* Floating Scroll to Story Button */}
      {showScrollToTop && (
        <button
          onClick={scrollToStory}
          className={`fixed bottom-20 right-4 z-110 bg-indigo-600 text-white shadow-lg 
          hover:bg-indigo-700 transition-all duration-200 hover:scale-110 ${
        showFullText ? 'px-4 py-3 rounded-full flex items-center gap-2' : 'p-3 rounded-full'
        }`}
          title="Return to Edit"
        >
          {scrollDirection === 'up' ? (
            <ArrowUpIcon className="w-6 h-6" />
          ) : (
            <ArrowDownIcon className="w-6 h-6" />
          )}
          {showFullText && (
            <span className="text-sm font-medium whitespace-nowrap">Return to Edit</span>
          )}
        </button>
      )}

      <input
        multiple={true}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={(event) => {
          setUploadingMediaCount((prev) => (prev || 0) + event.target.files!.length);
          setFilesToUpload(Array.from(event.target.files!));
        }}
      />
    </div>
  );
};
